import React, { useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Input } from 'reactstrap';

import apiReports, {
  IOpenWorkTypeMaterial,
  IOpenWorkTypeOffer,
  IOpenWorkTypes,
} from 'api/reports';
import PageContainer from 'components/layout/PageContainer';
import CurrencyToggle from 'components/offers/src/CurrencyToggle';
import formatGroupedNumber from 'services/numbers/formatGroupedNumber';
import { useReportCurrency } from 'services/offers/exchangeRate';
import getOfferStatusLabel from 'services/offers/offerStatusLabel';
import translations from 'services/translations/translations';

import { BarList, Legend, PALETTE } from './DashboardCharts';

import 'style/reports/OfferDashboard.css';
import 'style/reports/OpenWorkTypes.css';

// The preliminary offers and offers, together or one of them
const TYPE_FILTERS = ['all', 'preliminary-offer', 'offer'];

// The periods: the offers whose latest revision was made during the last months, or all
const PERIOD_MONTHS = [3, 6, 12, 24];
const DEFAULT_MONTHS = 12;

// A key of the department of an offer without one
const NO_DEPARTMENT = '';

// The materials without a known supplier: typed by hand, or a product without a supplier
const UNKNOWN = 'unknown';
const NO_SUPPLIER = 'no-supplier';

// The suppliers of the chart and the rows before "Show all"
const SUPPLIERS_CHARTED = 15;
const SUPPLIERS_SHOWN = 30;
const MATERIALS_SHOWN = 100;

interface ICell {
  offerIds: Set<number>;
  value: number;
}

interface ISupplierSummary {
  byDepartment: Map<string, ICell>;
  key: string;
  label: string;
  total: ICell;
}

const emptyCell = (): ICell => ({ offerIds: new Set<number>(), value: 0 });

// The sums are the cost prices of the materials (with their waste), not their sales
const addTo = (cell: ICell, material: IOpenWorkTypeMaterial) => {
  cell.offerIds.add(material.offerId);
  // eslint-disable-next-line no-param-reassign
  cell.value += material.expense;
};

const supplierKeyOf = (material: IOpenWorkTypeMaterial): string => {
  if (material.unknown) {
    return UNKNOWN;
  }

  return material.supplierId === null ? NO_SUPPLIER : `${material.supplierId}`;
};

/**
 * The open preliminary offers and offers (open, ready to send or sent) by supplier: the
 * cost prices of the materials of their positions summed by the supplier of the product in the registry and by
 * department. A row typed by hand, without a product of the registry, is "unknown". A
 * supplier or a cell chooses the materials shown below.
 */
const OpenSuppliers = ({ tabs }: { tabs?: JSX.Element }): JSX.Element => {
  const history = useHistory();
  const { t } = useTranslation([translations.offers, translations.common]);
  const tr = (key: string, options?: Record<string, unknown>) =>
    t(`${translations.offers}:dashboard.openSuppliers.${key}`, options);
  const trWork = (key: string, options?: Record<string, unknown>) =>
    t(`${translations.offers}:dashboard.openWorkTypes.${key}`, options);

  const [data, setData] = useState<IOpenWorkTypes | null>(null);
  const [failed, setFailed] = useState(false);
  const [typeFilter, setTypeFilter] = useState('all');
  const [months, setMonths] = useState<null | number>(DEFAULT_MONTHS);
  // The supplier and department of the materials shown, null for all
  const [supplier, setSupplier] = useState<null | string>(null);
  const [department, setDepartment] = useState<null | string>(null);
  // The department the whole view is limited to, null for all
  const [departmentFilter, setDepartmentFilter] = useState<null | string>(null);
  const [showAllSuppliers, setShowAllSuppliers] = useState(false);
  const [showAllMaterials, setShowAllMaterials] = useState(false);

  useEffect(() => {
    let active = true;
    setData(null);
    setFailed(false);
    apiReports
      .getOpenWorkTypes(months)
      .then((loaded) => active && setData(loaded))
      .catch(() => active && setFailed(true));

    return () => {
      active = false;
    };
  }, [months]);

  const offersById = useMemo(() => {
    const map = new Map<number, IOpenWorkTypeOffer>();
    (data?.offers ?? []).forEach((offer) => map.set(offer.id, offer));
    return map;
  }, [data]);

  const departmentOf = (offer?: IOpenWorkTypeOffer) =>
    offer?.departmentName ?? NO_DEPARTMENT;
  const departmentLabel = (key: string) => key || trWork('No department');
  const positionCodes = useMemo(() => {
    const map = new Map<number, string>();
    (data?.positions ?? []).forEach((position) =>
      map.set(position.id, position.code ?? '')
    );
    return map;
  }, [data]);

  // The sums in one currency: euros, or kronas when only Swedish offers are viewed
  const viewedOffers = (data?.offers ?? []).filter(
    (offer) =>
      (typeFilter === 'all' || offer.type === typeFilter) &&
      (departmentFilter === null || departmentOf(offer) === departmentFilter) &&
      (department === null || departmentOf(offer) === department)
  );

  // The departments of the choice: those with open offers of the chosen type
  const departmentChoices = Array.from(
    new Set(
      (data?.offers ?? [])
        .filter((offer) => typeFilter === 'all' || offer.type === typeFilter)
        .map((offer) => departmentOf(offer))
    )
  ).sort((a, b) => a.localeCompare(b, 'fi'));
  const report = useReportCurrency(viewedOffers.map((offer) => offer.country));
  const money = (value: number): string =>
    `${formatGroupedNumber(value, 0, 0)} ${report.symbol}`;

  // The materials of the offers of the chosen type, their sums in the currency shown
  const materials = useMemo(
    () =>
      (data?.materials ?? [])
        .filter((material) => {
          const offer = offersById.get(material.offerId);
          return (
            offer !== undefined &&
            (typeFilter === 'all' || offer.type === typeFilter) &&
            (departmentFilter === null ||
              departmentOf(offer) === departmentFilter)
          );
        })
        .map((material) => {
          const country = offersById.get(material.offerId)?.country;
          return {
            ...material,
            expense: report.convertFrom(country, material.expense),
            value: report.convertFrom(country, material.value),
          };
        }),
    [
      data,
      offersById,
      typeFilter,
      departmentFilter,
      report.currency,
      report.rate,
    ]
  );

  const departments = useMemo(
    () =>
      Array.from(
        new Set(
          materials.map((material) =>
            departmentOf(offersById.get(material.offerId))
          )
        )
      ).sort((a, b) => a.localeCompare(b, 'fi')),
    [materials, offersById]
  );
  const departmentColor = (key: string) =>
    PALETTE[Math.max(0, departments.indexOf(key)) % PALETTE.length];

  const supplierLabel = (key: string, name: null | string) => {
    if (key === UNKNOWN) {
      return tr('Unknown');
    }
    if (key === NO_SUPPLIER) {
      return tr('No supplier');
    }
    return name ?? key;
  };

  // The materials summed by supplier and department, the largest sales first; the unknown
  // and those without a supplier last
  const { suppliers, departmentTotals, total } = useMemo(() => {
    const bySupplier = new Map<string, ISupplierSummary>();
    const byDepartment = new Map<string, ICell>();
    const all = emptyCell();

    materials.forEach((material) => {
      const key = supplierKeyOf(material);
      const summary = bySupplier.get(key) ?? {
        byDepartment: new Map<string, ICell>(),
        key,
        label: supplierLabel(key, material.supplier),
        total: emptyCell(),
      };
      const departmentKey = departmentOf(offersById.get(material.offerId));
      const cell = summary.byDepartment.get(departmentKey) ?? emptyCell();
      addTo(cell, material);
      summary.byDepartment.set(departmentKey, cell);
      addTo(summary.total, material);
      bySupplier.set(key, summary);

      const departmentTotal = byDepartment.get(departmentKey) ?? emptyCell();
      addTo(departmentTotal, material);
      byDepartment.set(departmentKey, departmentTotal);
      addTo(all, material);
    });

    const isKnown = (summary: ISupplierSummary) =>
      summary.key !== UNKNOWN && summary.key !== NO_SUPPLIER;

    return {
      departmentTotals: byDepartment,
      suppliers: Array.from(bySupplier.values()).sort(
        (a, b) =>
          Number(isKnown(b)) - Number(isKnown(a)) ||
          b.total.value - a.total.value
      ),
      total: all,
    };
  }, [materials, offersById, t]);

  // The largest suppliers on the chart, and the unknown and those without a supplier always
  // at its end, so their share is seen
  const isSpecial = (summary: ISupplierSummary) =>
    summary.key === UNKNOWN || summary.key === NO_SUPPLIER;
  const chartedSuppliers = [
    ...suppliers
      .filter((summary) => !isSpecial(summary))
      .slice(0, SUPPLIERS_CHARTED),
    ...suppliers.filter(isSpecial),
  ];

  const shownSuppliers = showAllSuppliers
    ? suppliers
    : suppliers.slice(0, SUPPLIERS_SHOWN);

  const choose = (
    nextSupplier: null | string,
    nextDepartment: null | string
  ) => {
    const same = nextSupplier === supplier && nextDepartment === department;
    setSupplier(same ? null : nextSupplier);
    setDepartment(same ? null : nextDepartment);
    setShowAllMaterials(false);
  };
  const isChosen = (rowSupplier: null | string, column: null | string) =>
    supplier === rowSupplier && department === column;

  const chosenMaterials = materials
    .filter(
      (material) =>
        (supplier === null || supplierKeyOf(material) === supplier) &&
        (department === null ||
          departmentOf(offersById.get(material.offerId)) === department)
    )
    .sort((a, b) => b.expense - a.expense);
  const shownMaterials = showAllMaterials
    ? chosenMaterials
    : chosenMaterials.slice(0, MATERIALS_SHOWN);

  const cellContent = (cell: ICell | undefined) =>
    cell === undefined ? (
      <span className="open-work-types-empty">–</span>
    ) : (
      <>
        <span className="open-work-types-value">{money(cell.value)}</span>
        <span className="open-work-types-detail">
          {trWork('Offers', { count: cell.offerIds.size })}
        </span>
      </>
    );

  const unitLabel = (unit: null | string) =>
    unit ? t(`${translations.common}:units.${unit}`) : '';

  return (
    <PageContainer fullWidth>
      <div className="offer-dashboard">
        {tabs}
        <div className="offer-dashboard-header">
          <div>
            <h1>{tr('Title')}</h1>
            <p className="offer-dashboard-subtitle">{tr('Subtitle')}</p>
          </div>
          <div className="offer-dashboard-filters">
            {/* The whole view limited to one department */}
            <Input
              aria-label={trWork('Department')}
              onChange={(event) => {
                setDepartmentFilter(
                  event.target.value === '' ? null : event.target.value
                );
                setSupplier(null);
                setDepartment(null);
              }}
              type="select"
              value={departmentFilter ?? ''}
            >
              <option value="">{tr('All departments')}</option>
              {departmentChoices
                .filter((key) => key !== NO_DEPARTMENT)
                .map((key) => (
                  <option key={key} value={key}>
                    {key}
                  </option>
                ))}
            </Input>
            <CurrencyToggle
              base={report.currency}
              onChange={report.setCurrency}
              rate={report.rate}
              value={report.currency}
            />
            <div className="offer-dashboard-segments" role="group">
              {[...PERIOD_MONTHS, null].map((key) => (
                <button
                  className={key === months ? 'active' : ''}
                  key={key ?? 'all'}
                  onClick={() => setMonths(key)}
                  type="button"
                >
                  {key === null
                    ? trWork('All periods')
                    : t(`${translations.offers}:dashboard.Months`, {
                        count: key,
                      })}
                </button>
              ))}
            </div>
            <div className="offer-dashboard-segments" role="group">
              {TYPE_FILTERS.map((key) => (
                <button
                  className={key === typeFilter ? 'active' : ''}
                  key={key}
                  onClick={() => setTypeFilter(key)}
                  type="button"
                >
                  {trWork(`types.${key}`)}
                </button>
              ))}
            </div>
          </div>
        </div>

        {failed ? <p>{trWork('Load failed')}</p> : null}
        {data === null && !failed ? <p>{trWork('Loading')}</p> : null}

        {data !== null ? (
          <>
            <section className="offer-dashboard-card offer-dashboard-table-card">
              <h2>{tr('By supplier')}</h2>
              <p className="offer-dashboard-card-info">
                {tr('Info')}
                {data.restrictedCount > 0
                  ? ` ${trWork('Restricted', {
                      count: data.restrictedCount,
                    })}`
                  : ''}
              </p>
              {suppliers.length === 0 ? (
                <p>{trWork('No open offers')}</p>
              ) : (
                <>
                  <BarList
                    bars={chartedSuppliers.map((summary) => ({
                      color: PALETTE[0],
                      key: summary.key,
                      label: summary.label,
                      parts: departments.map((key) => ({
                        color: departmentColor(key),
                        label: `${departmentLabel(key)}: ${money(
                          summary.byDepartment.get(key)?.value ?? 0
                        )}`,
                        value: summary.byDepartment.get(key)?.value ?? 0,
                      })),
                      text: money(summary.total.value),
                      value: summary.total.value,
                    }))}
                    onSelect={(key) => choose(key, null)}
                    selected={department === null ? supplier : null}
                  />
                  <Legend
                    series={departments.map((key) => ({
                      color: departmentColor(key),
                      key,
                      label: departmentLabel(key),
                    }))}
                  />
                  <div className="offer-dashboard-table-wrapper">
                    <table className="offer-dashboard-table open-work-types-table">
                      <thead>
                        <tr>
                          <th>{tr('Supplier')}</th>
                          {departments.map((key) => (
                            <th className="numeric" key={key}>
                              {departmentLabel(key)}
                            </th>
                          ))}
                          <th className="numeric total">{trWork('Total')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {shownSuppliers.map((summary) => (
                          <tr key={summary.key}>
                            <td
                              className={`target ${
                                isChosen(summary.key, null) ? 'chosen' : ''
                              }`}
                              onClick={() => choose(summary.key, null)}
                            >
                              {summary.label}
                            </td>
                            {departments.map((key) => (
                              <td
                                className={`numeric ${
                                  isChosen(summary.key, key) ? 'chosen' : ''
                                }`}
                                key={key}
                                onClick={() => choose(summary.key, key)}
                              >
                                {cellContent(summary.byDepartment.get(key))}
                              </td>
                            ))}
                            <td
                              className={`numeric total ${
                                isChosen(summary.key, null) ? 'chosen' : ''
                              }`}
                              onClick={() => choose(summary.key, null)}
                            >
                              {cellContent(summary.total)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr>
                          <td
                            className={`target ${
                              isChosen(null, null) ? 'chosen' : ''
                            }`}
                            onClick={() => choose(null, null)}
                          >
                            {trWork('Total')}
                          </td>
                          {departments.map((key) => (
                            <td
                              className={`numeric ${
                                isChosen(null, key) ? 'chosen' : ''
                              }`}
                              key={key}
                              onClick={() => choose(null, key)}
                            >
                              {cellContent(departmentTotals.get(key))}
                            </td>
                          ))}
                          <td className="numeric total">
                            {cellContent(total)}
                          </td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                  {suppliers.length > SUPPLIERS_SHOWN ? (
                    <button
                      className="offer-dashboard-show-all"
                      onClick={() => setShowAllSuppliers(!showAllSuppliers)}
                      type="button"
                    >
                      {showAllSuppliers
                        ? t(`${translations.offers}:dashboard.Show less`)
                        : t(`${translations.offers}:dashboard.Show all`, {
                            count: suppliers.length,
                          })}
                    </button>
                  ) : null}
                </>
              )}
            </section>

            <section className="offer-dashboard-card offer-dashboard-table-card">
              <h2>
                {tr('Materials', { count: chosenMaterials.length })}
                {supplier !== null || department !== null ? (
                  <button
                    className="open-work-types-filter"
                    onClick={() => choose(null, null)}
                    type="button"
                  >
                    {[
                      supplier === null
                        ? null
                        : suppliers.find((summary) => summary.key === supplier)
                            ?.label ?? supplier,
                      department === null ? null : departmentLabel(department),
                    ]
                      .filter((label) => label !== null)
                      .join(' · ')}
                    {' ×'}
                  </button>
                ) : null}
              </h2>
              <div className="offer-dashboard-table-wrapper">
                <table className="offer-dashboard-table">
                  <thead>
                    <tr>
                      <th>{tr('Supplier')}</th>
                      <th>{tr('Material')}</th>
                      <th>{trWork('Offer')}</th>
                      <th>{trWork('Department')}</th>
                      <th>{trWork('Status')}</th>
                      <th>{trWork('Position')}</th>
                      <th className="numeric">{trWork('Quantity')}</th>
                      <th className="numeric">{tr('Cost')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {shownMaterials.map((material) => {
                      const offer = offersById.get(material.offerId);
                      return (
                        <tr
                          key={material.id}
                          onClick={() =>
                            offer
                              ? history.push(
                                  `/offers/${offer.id}/${offer.type}`
                                )
                              : undefined
                          }
                        >
                          <td>
                            {supplierLabel(
                              supplierKeyOf(material),
                              material.supplier
                            )}
                          </td>
                          <td>{material.name ?? '–'}</td>
                          <td className="target">
                            {offer?.targetName}
                            <span className="open-work-types-type">
                              {offer
                                ? t(
                                    `${translations.offers}:phases.${offer.type}`
                                  )
                                : ''}
                            </span>
                          </td>
                          <td>{departmentLabel(departmentOf(offer))}</td>
                          <td>
                            {offer
                              ? getOfferStatusLabel(t, offer.status, offer.type)
                              : ''}
                          </td>
                          <td>{positionCodes.get(material.positionId)}</td>
                          <td className="numeric">
                            {`${formatGroupedNumber(
                              material.quantity,
                              2,
                              0
                            )} ${unitLabel(material.unit)}`.trim()}
                          </td>
                          <td className="numeric">{money(material.expense)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              {chosenMaterials.length > MATERIALS_SHOWN ? (
                <button
                  className="offer-dashboard-show-all"
                  onClick={() => setShowAllMaterials(!showAllMaterials)}
                  type="button"
                >
                  {showAllMaterials
                    ? t(`${translations.offers}:dashboard.Show less`)
                    : t(`${translations.offers}:dashboard.Show all`, {
                        count: chosenMaterials.length,
                      })}
                </button>
              ) : null}
            </section>
          </>
        ) : null}
      </div>
    </PageContainer>
  );
};

export default OpenSuppliers;
